import React from 'react'
import Stack from '@mui/material/Stack'
import LineChart from './LineChart'
import Kpi from './Kpi'
import Grid from '@mui/material/Grid'
import SolarPowerIcon from '@mui/icons-material/SolarPower'
import BoltIcon from '@mui/icons-material/Bolt'
import EnergySavingsLeafIcon from '@mui/icons-material/EnergySavingsLeaf'
import BatteryCharging80Icon from '@mui/icons-material/BatteryCharging80'
import EuroIcon from '@mui/icons-material/Euro'
import SavingsIcon from '@mui/icons-material/Savings'
import PieChart from './PieChart'
import ResolutionPicker from './ResolutionPicker'
import { useState } from 'react'

interface Props {
  setError: React.Dispatch<any>
  ready?: Boolean
}

const getPvVsEldemandData = (lineData: any) => {
  const lines = []
  lines.push(lineData[0].find((el: any) => el.id.includes('eldemand')))
  lineData.forEach((arr: any, index: any) => {
    let obj = arr.find((el: any) => el?.id === 'pv_total_power')
    if (obj) obj.id = 'pv_total_power'
    lines.push(obj)
  })
  return lines.reverse()
}

export function Dashboard({ ready, setError }: Props) {
  const [data, setData] = useState<any>(undefined)
  const [showPicker, setShowPicker] = useState<Boolean>(false)

  React.useEffect(() => {
    if (ready) setShowPicker(true)
  }, [ready])

  return (
    <>
      {showPicker && <ResolutionPicker setData={setData} setError={setError} />}
      {data?.kpis && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Kpi
            icon={<SolarPowerIcon sx={{ fontSize: 25, mr: 2 }} />}
            title={'Total PV production'}
            values={data.kpis.map((kpi: any) =>
              kpi.total_pv !== null ? `${(kpi.total_pv / 1000).toFixed(2)} kWh` : null
            )}
          />
          <Kpi
            icon={<BoltIcon sx={{ fontSize: 27, mr: 1.5 }} />}
            title={'Total Electricity demand'}
            values={data.kpis.map((kpi: any) =>
              kpi.total_eldemand !== null ? `${(kpi.total_eldemand / 1000).toFixed(2)} kWh` : null
            )}
          />
          <Kpi
            icon={<SolarPowerIcon sx={{ fontSize: 25, mr: 2 }} />}
            title={'Self consumption PV'}
            values={data.kpis.map((kpi: any) =>
              kpi.self_consumption_pv !== null ? `${(kpi.self_consumption_pv * 100).toFixed(2)} %` : null
            )}
          />
          <Kpi
            icon={<EnergySavingsLeafIcon sx={{ fontSize: 25, mr: 2 }} />}
            title={'Self consumption'}
            values={data.kpis.map((kpi: any) =>
              kpi.self_consumption !== null ? `${(kpi.self_consumption * 100).toFixed(2)} %` : null
            )}
            info={'Self consumption based on PV output and cheap energy from the grid stored in battery.'}
          />
          <Kpi
            icon={<BatteryCharging80Icon sx={{ fontSize: 25, mr: 2 }} />}
            title={'Battery storage grid'}
            values={data.kpis.map((kpi: any) =>
              kpi.battery_storage_grid !== null ? `${(kpi.battery_storage_grid / 1000).toFixed(2)} kWh` : null
            )}
          />
          <Kpi
            icon={<BatteryCharging80Icon sx={{ fontSize: 25, mr: 2 }} />}
            title={'Battery storage solar'}
            values={data.kpis.map((kpi: any) =>
              kpi.battery_storage_solar !== null ? `${(kpi.battery_storage_solar / 1000).toFixed(2)} kWh` : null
            )}
          />
          <Kpi
            icon={<EuroIcon sx={{ fontSize: 25, mr: 2 }} />}
            title={'Total costs'}
            values={data.kpis.map((kpi: any) => (kpi.total_costs !== null ? `${kpi.total_costs.toFixed(2)} €` : null))}
          />
          <Kpi
            icon={<SavingsIcon sx={{ fontSize: 25, mr: 2 }} />}
            title={'Total savings'}
            values={data.kpis.map((kpi: any) =>
              kpi.total_savings !== null ? `${kpi.total_savings.toFixed(2)} €` : null
            )}
          />
        </Grid>
      )}

      {data?.lineData && (
        <Stack alignItems={'center'}>
          {data?.lineData[0].map((line: any, index: any) => {
            return <LineChart key={index} data={[line]} hideLegend />
          })}
          <LineChart
            colors={{ scheme: 'set1' }}
            yLabel='Power [kWh]'
            title='Pv Production vs. Eldemand [kWh]'
            data={getPvVsEldemandData(data.lineData)}
          />
        </Stack>
      )}

      {data?.kpis && (
        <Grid container alignItems={'center'} sx={{ mt: 3, width: '100%', height: '100%' }}>
          {data.kpis.map((kpi: any, index: any) => (
            <Grid item xs={12} key={index}>
              <PieChart
                title={'Consumption Distribution'}
                colors={{ scheme: 'set1' }}
                data={[
                  {
                    id: 'pv',
                    label: 'PV',
                    value: (kpi.self_consumption_pv * 100).toFixed(2),
                    color: '#ffcd38',
                  },
                  {
                    id: 'battery',
                    label: 'Battery',
                    value: ((kpi.self_consumption - kpi.self_consumption_pv) * 100).toFixed(2),
                    color: '#4dabf5',
                  },
                  {
                    id: 'grid',
                    label: 'Grid',
                    value: (100 - kpi.self_consumption * 100).toFixed(2),
                    color: '#f6685e',
                  },
                ]}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </>
  )
}

export const MemoDashboard = React.memo(Dashboard)
